import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [state, setState] = useState({ loading: true, user: null, orgs: [] });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setState({ loading: false, user: data.user, orgs: data.organizations || [] });
      if (data.user?.active_org_id) {
        localStorage.setItem("gc_active_org", data.user.active_org_id);
      }
      return data;
    } catch (e) {
      setState({ loading: false, user: null, orgs: [] });
      return null;
    }
  }, []);

  useEffect(() => {
    // Skip /me if returning from Google callback
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.user?.active_org_id) localStorage.setItem("gc_active_org", data.user.active_org_id);
    await refresh();
    return data.user;
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("gc_active_org");
    setState({ loading: false, user: null, orgs: [] });
  }, []);

  const switchOrg = useCallback(async (orgId) => {
    const { data } = await api.post("/auth/switch-org", { org_id: orgId });
    localStorage.setItem("gc_active_org", orgId);
    setState((s) => ({ ...s, user: data.user }));
    return data.user;
  }, []);

  const value = useMemo(() => ({ ...state, refresh, login, logout, switchOrg, formatErr }),
    [state, refresh, login, logout, switchOrg]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export const ROLES = {
  super_admin: "Super Administrator",
  admin_mitra: "Admin Mitra",
  operator: "Operator Produksi",
  keuangan: "Staf Keuangan",
  manajer: "Manajer",
  auditor: "Auditor",
  pengabdian: "Tim Pengabdian",
};

export function canEditMaster(user) {
  if (!user) return false;
  if (user.platform_role === "super_admin") return true;
  return ["admin_mitra", "keuangan"].includes(user.active_role);
}

export function canEditProduction(user) {
  if (!user) return false;
  if (user.platform_role === "super_admin") return true;
  return ["admin_mitra", "operator", "keuangan"].includes(user.active_role);
}

export function canApprove(user) {
  if (!user) return false;
  if (user.platform_role === "super_admin") return true;
  return ["admin_mitra", "manajer"].includes(user.active_role);
}
